import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Globe2, Factory, Star, Wrench, MapPin, Award, Flame, Hammer, Building2 } from "lucide-react";
import { fetchSliders, fetchBlogList, fetchClients } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Section, SectionHead } from "@/components/Section";
import ProductPlaceholder from "@/components/ProductPlaceholder";

// Curated, verified Unsplash imagery (all alt-text validated for industrial/tools content)
const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/3922d02f-8cc0-4292-aa2d-88817fa153ce/images/ae2cea847a4ab9e6f56262c2ce5bf428ecce9503070f964acc6255b30caa0b33.png",
  // Workshop / mechanic in use
  workshop1: "https://images.unsplash.com/photo-1698382318239-2b134ca8fa4c?w=1600&q=80", // wrenches hanging on a rack
  workshop2: "https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=1600&q=80", // pliers/tools array
  hands: "https://images.unsplash.com/photo-1611288875785-f62fb9b044a7?w=1600&q=80",     // person holding a wrench
  worker: "https://images.unsplash.com/photo-1613206485381-b028e578e791?w=1600&q=80",    // wrenches arranged in circle
  // Spanners / sockets / specs
  sockets: "https://images.unsplash.com/photo-1503789146722-cf137a3c0fea?w=1600&q=80",   // adjustable + pipe wrenches
  tools1: "https://images.unsplash.com/photo-1742828732550-9d708214bbc9?w=1600&q=80",    // wrenches on orange/black — brand match
  wrenchSet: "https://images.unsplash.com/photo-1503791774117-08c379dd7f7c?w=1600&q=80", // two silver open wrenches
  // Manufacturing / dark moody
  forging: "https://images.unsplash.com/photo-1751826391434-db24149bffc3?w=1600&q=80",   // blacksmith hammering at anvil — drop-forge feel
  facility: "https://images.unsplash.com/photo-1644079446600-219068676743?w=1600&q=80",  // warehouse / racking — long aisle of shelves
  toolbox: "https://images.unsplash.com/photo-1637640125496-31852f042a60?w=1600&q=80",   // wrenches in box
};

const cleanCat = (t) => t.split("Manufacturer")[0].split("Hand Tool")[0].trim() || t;

const OFFICES = [
  { city: "Noida", country: "India", role: "Corporate HQ", address: "A-147, Sector 83, Noida, Uttar Pradesh, 201305", flag: "🇮🇳", primary: true },
  { city: "New Delhi", country: "India", role: "North India Sales", address: "Delhi NCR · India", flag: "🇮🇳" },
  { city: "Mumbai", country: "India", role: "West India Sales", address: "Maharashtra · India", flag: "🇮🇳" },
  { city: "Bengaluru", country: "India", role: "South India Sales", address: "Karnataka · India", flag: "🇮🇳" },
  { city: "Chennai", country: "India", role: "South-East Sales", address: "Tamil Nadu · India", flag: "🇮🇳" },
  { city: "Dubai", country: "UAE", role: "Middle East Operations", address: "Dubai · United Arab Emirates", flag: "🇦🇪" },
];

const Home = () => {
  const { site, categories } = useSite();
  const [sliders, setSliders] = useState([]);
  const [latestBlog, setLatestBlog] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, b, c] = await Promise.all([
        fetchSliders(),
        fetchBlogList({ limit: 3 }),
        fetchClients(),
      ]);
      setSliders(s);
      setLatestBlog(b.items || []);
      setClients(c || []);
    })();
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative bg-[var(--jt-ink)] text-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center opacity-55" style={{ backgroundImage: `url(${IMG.hero})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--jt-ink)] via-[var(--jt-ink)]/85 to-transparent" aria-hidden="true" />
        <div className="relative jt-container py-20 lg:py-32 min-h-[72vh] flex flex-col justify-center">
          <div className="max-w-3xl">
            <div className="eyebrow jt-rise">Est. 1961 · Pioneers in Indian Hand Tools</div>
            <h1 className="font-display font-bold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-3 jt-rise jt-rise-1">
              India's hand-tools,<br/>
              <span className="text-[var(--jt-primary)]">engineered to outwork the world.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-white/80 max-w-xl jt-rise jt-rise-2">
              Spanners. Pliers. Hammers. Screwdrivers. Sockets. Over <strong>100 SKUs</strong>, 22 categories, chrome-vanadium drop-forged — trusted by India's largest industrial buyers and <strong>exported worldwide</strong>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 jt-rise jt-rise-3">
              <Link to="/products" className="jt-btn jt-btn-primary" data-testid="hero-cta-products">
                Explore Products <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="jt-btn jt-btn-ghost-light" data-testid="hero-cta-quote">
                Request Bulk Quote
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl jt-rise jt-rise-4" data-testid="hero-stats">
              {[
                { k: "60+", v: "Years of Trust" },
                { k: "100+", v: "SKUs" },
                { k: "Global", v: "Worldwide Exports" },
              ].map((s) => (
                <div key={s.v} className="border-l-2 border-[var(--jt-primary)] pl-3">
                  <div className="font-display font-bold text-3xl lg:text-4xl">{s.k}</div>
                  <div className="font-mono text-[0.65rem] tracking-widest uppercase text-white/60 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[var(--jt-primary)] text-white overflow-hidden border-y-2 border-black/10" data-testid="marquee">
        <div className="jt-marquee py-3 whitespace-nowrap font-display font-bold uppercase tracking-wider text-sm">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="flex">
              {["Pioneers in Spanners since 1961", "Cold-Forged Sockets · Made in India", "Drop-Forged Chrome Vanadium", "ISO-Aligned Quality", "Exporting Worldwide", "Now serving as Jhalani Hand Tools"].map(
                (txt, j) => (
                  <span key={j} className="px-8 flex items-center gap-8">
                    {txt}<span className="w-2 h-2 bg-black/40 rounded-full inline-block" />
                  </span>
                )
              )}
            </span>
          ))}
        </div>
      </div>

      {/* PIONEER 1 — SPANNERS (video left) */}
      <section className="bg-[var(--jt-ink)] text-white relative overflow-hidden" data-testid="pioneer-spanners">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[600px]">
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-square lg:aspect-auto lg:h-full overflow-hidden bg-black">
              <video
                src="https://videos.pexels.com/video-files/5846395/5846395-sd_360_640_25fps.mp4"
                poster="https://images.pexels.com/videos/5846395/pexels-photo-5846395.jpeg?auto=compress&cs=tinysrgb&w=900"
                autoPlay muted loop playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-black/30 pointer-events-none" />
              <div className="absolute top-4 left-4 bg-[var(--jt-primary)] text-white font-mono text-[0.65rem] tracking-widest uppercase px-3 py-1.5">
                Pioneer 01 · Spanners · Since 1961
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 p-10 lg:p-16 xl:p-20 flex flex-col justify-center">
            <div className="eyebrow">India's First</div>
            <h2 className="mt-3 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              The first<br/>
              <span className="text-[var(--jt-primary)]">drop-forged spanners</span><br/>
              made in India.
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed text-base lg:text-lg max-w-xl">
              In <strong>1961</strong>, while India's industries were still importing precision
              hand tools, Jhalani pioneered the country's <strong>first generation of
              drop-forged spanners</strong>. From a single forging press in our early Noida
              workshop, the Jhalani spanner became the workshop standard for India's steel
              mills, railways and heavy industries.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-md">
              {[
                { Icon: Award, k: "1961", v: "First spanner" },
                { Icon: Hammer, k: "Drop-Forged", v: "Cr-V steel" },
                { Icon: Factory, k: "60+", v: "Years running" },
              ].map(({ Icon, k, v }) => (
                <div key={v} className="bg-[var(--jt-ink)] p-4">
                  <Icon className="w-5 h-5 text-[var(--jt-primary)]" strokeWidth={1.5} />
                  <div className="mt-2 font-display font-bold text-base">{k}</div>
                  <div className="font-mono text-[0.6rem] tracking-widest uppercase text-white/50">{v}</div>
                </div>
              ))}
            </div>
            <Link to="/category/spanners" className="mt-8 inline-flex jt-btn jt-btn-primary self-start" data-testid="pioneer-spanners-cta">
              Shop Spanners <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PIONEER 2 — COLD-FORGED SOCKETS (video right) */}
      <section className="bg-[#0F0F0F] text-white relative overflow-hidden border-t border-white/5" data-testid="pioneer-sockets">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[600px]">
          <div className="lg:col-span-6 p-10 lg:p-16 xl:p-20 flex flex-col justify-center order-2 lg:order-1">
            <div className="eyebrow">India's First</div>
            <h2 className="mt-3 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              The first <span className="text-white">commercial</span><br/>
              <span className="text-[var(--jt-primary)]">cold-forged sockets</span><br/>
              made in India.
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed text-base lg:text-lg max-w-xl">
              Cold-forging is where precision meets brute strength — the metallurgy that lets a
              socket take torque a casting never could. Jhalani brought
              <strong> cold-forged impact sockets</strong> to India for the first time, with the
              dimensional tolerances and bi-hex geometry international OEM buyers demand. Today
              our cold-forging lines still run alongside the original drop-forging presses in
              presses in India.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-md">
              {[
                { Icon: Award, k: "First", v: "in India" },
                { Icon: Hammer, k: "Cold-Forged", v: "Impact sockets" },
                { Icon: Globe2, k: "30+", v: "Export markets" },
              ].map(({ Icon, k, v }) => (
                <div key={v} className="bg-[#0F0F0F] p-4">
                  <Icon className="w-5 h-5 text-[var(--jt-primary)]" strokeWidth={1.5} />
                  <div className="mt-2 font-display font-bold text-base">{k}</div>
                  <div className="font-mono text-[0.6rem] tracking-widest uppercase text-white/50">{v}</div>
                </div>
              ))}
            </div>
            <Link to="/products" className="mt-8 inline-flex jt-btn jt-btn-primary self-start" data-testid="pioneer-sockets-cta">
              Shop Sockets <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6 relative order-1 lg:order-2">
            <div className="relative aspect-square lg:aspect-auto lg:h-full overflow-hidden bg-black">
              <video
                poster="/videos/sockets-poster.jpg?v=5"
                autoPlay muted loop playsInline
                className="w-full h-full object-cover"
                data-testid="sockets-video"
              >
                <source src="/videos/sockets-loop-mobile.mp4?v=5" media="(max-width: 768px)" type="video/mp4" />
                <source src="/videos/sockets-loop.mp4?v=5" type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-tl from-black/60 via-transparent to-black/30 pointer-events-none" />
              <div className="absolute top-4 right-4 bg-[var(--jt-primary)] text-white font-mono text-[0.65rem] tracking-widest uppercase px-3 py-1.5">
                Pioneer 02 · Cold-Forged Sockets
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY SHOWCASE */}
      <Section>
        <SectionHead
          eyebrow="Built for every workshop"
          title="Hero categories"
          intro="Our most-sought product lines, refined over six decades for industrial-grade reliability."
          action={
            <Link to="/catalogue" className="hidden md:inline-flex jt-btn jt-btn-outline" data-testid="cats-view-all">
              View Full Catalogue <ArrowRight className="w-4 h-4" />
            </Link>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(sliders.length ? sliders : categories.slice(0, 3)).map((s, idx) => {
            const slug = s.slug || (categories[idx] && categories[idx].slug) || "";
            // Map category slug -> representative product photo (matches /app/backend/seed_data/product_assets_map.json)
            const photoBySlug = {
              "spanners": "p-28.jpg",
              "sockets-and-accessories": "p-30.jpg",
              "chisels": "p-02.jpg",
              "hammers": "p-03.jpg",
              "wheel-spanners": "p-17.jpg",
              "pipe-wrenches": "p-09.jpg",
              "allen-key-tools": "p-28.jpg",
              "bench-vice-and-clamp": "p-13.jpg",
              "plier-and-pincer": "p-01.jpg",
              "cutters": "p-13.jpg",
              "screw-drivers": "p-20.jpg",
              "lubricants-tools": "p-21.jpg",
              "tool-box-and-tool-trolley": "p-17.jpg",
              "bearing-puller": "p-17.jpg",
              "punches": "p-02.jpg",
              "ratchet-die-set": "p-19.jpg",
              "filter-wrench": "p-22.jpg",
              "feeler-gauge": "p-15.jpg",
              "jack-plane-and-block-plane": "p-19.jpg",
              "step-drill": "p-19.jpg",
              "measuring-tools": "p-15.jpg",
              "non-sparking-tools": "p-25.jpg",
            };
            const photo = photoBySlug[slug] || "p-28.jpg";
            return (
              <Link
                key={slug || idx}
                to={`/category/${slug}`}
                data-testid={`hero-slide-${s.slug}`}
                className="relative group block aspect-[4/5] bg-white text-white overflow-hidden border border-[var(--jt-border)]"
              >
                <img
                  src={`/products/photos/${photo}`}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-contain p-8 group-hover:scale-[1.04] transition-transform duration-500"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="eyebrow !text-[var(--jt-primary)]">Category</div>
                  <h3 className="font-display font-bold uppercase text-3xl mt-1 leading-tight">{s.title}</h3>
                  <div className="mt-3 inline-flex items-center gap-2 font-mono text-xs tracking-widest uppercase">
                    Explore Line <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* APPLICATION STRIP — India-context imagery */}
      <section className="bg-white">
        <div className="jt-container pt-16 lg:pt-20 pb-6">
          <div className="eyebrow">In Use Across India</div>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-2 leading-tight">
            From Delhi garages to Mumbai shop floors.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--jt-border)]">
          {[
            { src: "https://images.unsplash.com/photo-1723099971299-3789db53604c?w=1200&q=80", label: "Auto Garages" },
            { src: "https://images.unsplash.com/photo-1765447650934-e7d49378fd66?w=1200&q=80", label: "Two-Wheeler Workshops" },
            { src: "https://images.unsplash.com/photo-1767339736277-980a7d7d1fe5?w=1200&q=80", label: "Street Mechanics" },
            { src: "https://images.unsplash.com/photo-1767789083299-28d17c4ffb7a?w=1200&q=80", label: "Industrial Plants" },
          ].map((a) => (
            <div key={a.label} className="relative aspect-[4/5] md:aspect-[3/4] bg-[var(--jt-bg)] group overflow-hidden">
              <img
                src={a.src}
                alt={`${a.label} — Jhalani tools in use across India`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="font-mono text-[0.6rem] tracking-widest text-[var(--jt-primary)] uppercase">In Use · India</div>
                <div className="font-display font-bold text-white text-lg mt-1">{a.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ALL CATEGORIES STRIP */}
      <Section>
        <SectionHead
          eyebrow="Full Catalogue"
          title="22 categories. 100+ SKUs."
          intro="Every tool an industrial buyer or workshop needs — under one roof."
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-px bg-[var(--jt-border)] border border-[var(--jt-border)]">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group bg-white p-5 hover:bg-[var(--jt-ink)] hover:text-white transition-colors"
              data-testid={`cat-tile-${c.slug}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[0.65rem] tracking-widest uppercase text-[var(--jt-text-muted)] group-hover:text-white/60">
                  {String(c.product_count).padStart(2, "0")} products
                </div>
                <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <h3 className="font-display font-bold text-base leading-snug uppercase">{cleanCat(c.title)}</h3>
            </Link>
          ))}
        </div>
      </Section>

      {/* TRUST / WHY */}
      <Section dark>
        <SectionHead
          dark
          eyebrow="Why Jhalani"
          title="Forged for the long haul"
          intro="From the steel selection to the final chrome plating, every Jhalani tool is built to outwork ten lesser ones."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { Icon: ShieldCheck, title: "Drop-Forged", body: "Chrome-vanadium steel, fully hardened & tempered. Generally conforming to IS 2028 / IS 2029 / DIN." },
            { Icon: Factory, title: "Made in India", body: "In-house forging, heat-treatment, plating and QC — full control over every step of the build." },
            { Icon: Globe2, title: "30+ Export Markets", body: "Distributor networks across Middle East, Africa, South-East Asia and Europe." },
            { Icon: Star, title: "Trusted by Industry", body: "Powering shop floors at ABB, ACC, Ambuja, SAIL, ONGC, Reliance, Tata and many more." },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="border border-white/10 p-6 hover:border-[var(--jt-primary)] transition-colors" data-testid={`why-${title.toLowerCase().replace(/\s+/g, '-')}`}>
              <Icon className="w-8 h-8 text-[var(--jt-primary)]" strokeWidth={1.5} />
              <h3 className="mt-4 font-display font-bold uppercase tracking-tight">{title}</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* MANUFACTURING SPLIT */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="eyebrow">Inside the Factory</div>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-2 leading-tight">
              Where every tool starts as steel,<br/>
              and leaves as a Jhalani.
            </h2>
            <p className="mt-5 text-[var(--jt-text-muted)] leading-relaxed">
              Our Indian facility runs forging presses, induction hardening lines, vibratory polishing drums and chrome electroplating in one continuous flow — letting us hold quality from raw billet to finished tool without a handoff.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[{ k: "5+", v: "Forging lines" }, { k: "100%", v: "QC tested" }, { k: "60yr", v: "Heritage" }].map((m) => (
                <div key={m.v} className="border-l-2 border-[var(--jt-primary)] pl-3">
                  <div className="font-display font-bold text-2xl">{m.k}</div>
                  <div className="font-mono text-[0.65rem] tracking-widest uppercase text-[var(--jt-text-muted)] mt-1">{m.v}</div>
                </div>
              ))}
            </div>
            <Link to="/manufacturing" className="mt-8 inline-flex jt-btn jt-btn-outline" data-testid="mfg-cta">
              Tour the Facility <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="aspect-[3/4] bg-cover bg-center" style={{ backgroundImage: `url(${IMG.facility})` }} />
            <div className="grid grid-rows-2 gap-2">
              <div className="bg-cover bg-center" style={{ backgroundImage: `url(${IMG.forging})` }} />
              <div className="bg-cover bg-center" style={{ backgroundImage: `url(${IMG.tools1})` }} />
            </div>
          </div>
        </div>
      </Section>

      {/* LOCATE US — Offices */}
      <Section dark>
        <SectionHead
          dark
          eyebrow="Locate Us"
          title="Offices across India & UAE"
          intro="Sales, support and despatch — wherever our customers are."
          action={
            <Link to="/contact" className="hidden md:inline-flex jt-btn jt-btn-ghost-light" data-testid="locate-cta">
              Visit Us <ArrowRight className="w-4 h-4" />
            </Link>
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="offices-grid">
          {OFFICES.map((o) => (
            <div
              key={o.city}
              className={`p-7 transition-colors ${o.primary ? "bg-[var(--jt-primary)]" : "bg-[var(--jt-ink)] hover:bg-[#1a1a1a]"}`}
              data-testid={`office-${o.city.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl leading-none">{o.flag}</span>
                  <Building2 className="w-4 h-4 text-white/60" strokeWidth={1.5} />
                </div>
                {o.primary && (
                  <span className="font-mono text-[0.6rem] tracking-widest uppercase bg-black/40 px-2 py-1">HQ</span>
                )}
              </div>
              <div className="mt-5">
                <div className={`font-display font-bold text-2xl ${o.primary ? "text-white" : "text-white"}`}>
                  {o.city}, <span className="opacity-70">{o.country}</span>
                </div>
                <div className={`mt-1 font-mono text-[0.65rem] tracking-widest uppercase ${o.primary ? "text-black/70" : "text-[var(--jt-primary)]"}`}>
                  {o.role}
                </div>
                <div className={`mt-4 flex items-start gap-2 text-sm leading-relaxed ${o.primary ? "text-white/90" : "text-white/70"}`}>
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{o.address}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* BLOG TEASER */}
      <Section className="!pt-14">
        <SectionHead
          eyebrow="Workshop Journal"
          title="Latest from our blog"
          action={<Link to="/blog" className="hidden md:inline-flex jt-btn jt-btn-outline" data-testid="blog-view-all">All Posts <ArrowRight className="w-4 h-4" /></Link>}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {latestBlog.map((b, i) => (
            <Link key={b.slug} to={`/blog/${b.slug}`} className="jt-card group block" data-testid={`blog-tile-${b.slug}`}>
              <div
                className="aspect-[16/10] bg-cover bg-center overflow-hidden"
                style={{ backgroundImage: `url(${[IMG.tools1, IMG.sockets, IMG.workshop1][i % 3]})` }}
              />
              <div className="p-5">
                <div className="font-mono text-[0.65rem] tracking-widest uppercase text-[var(--jt-text-muted)]">{b.date || "Journal"} · {b.author}</div>
                <h3 className="mt-2 font-display font-bold uppercase text-base leading-tight line-clamp-2 group-hover:text-[var(--jt-primary)]">{b.title}</h3>
                <p className="mt-2 text-sm text-[var(--jt-text-muted)] line-clamp-3">{b.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Final CTA strip */}
      <section className="relative bg-[var(--jt-ink)] text-white overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${IMG.forging})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--jt-ink)] via-[var(--jt-ink)]/80 to-transparent" />
        <div className="relative jt-container py-20 lg:py-28">
          <div className="max-w-2xl">
            <div className="eyebrow">Bulk · Export · OEM · Dealer</div>
            <h2 className="mt-3 font-display font-bold tracking-tight text-4xl lg:text-6xl leading-tight">
              Ready to put a <span className="text-[var(--jt-primary)]">Jhalani</span> in your workshop?
            </h2>
            <p className="mt-5 text-white/80 max-w-xl">Talk to our team about pricing, custom branding, OEM packs, or starting a dealer territory in your city.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="jt-btn jt-btn-primary" data-testid="final-cta-quote">Request a Quote <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/dealers" className="jt-btn jt-btn-ghost-light" data-testid="final-cta-dealer">Become a Dealer</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
